/** Billing page + pricing/estimate UI (pages/billing.tsx, components/billing/*). */
export const billing = {
  // Interval words, reused by the configurator and the subscription summary.
  interval: {
    year: "year",
    month: "month",
  },
  total: "Total",

  // pages/billing.tsx — page header
  page: {
    title: "Billing & Licenses",
    description:
      "One plan: pay per player device, every feature included. Licenses are shared across all the workspaces you own.",
  },

  error: {
    loadTitle: "Couldn't load billing",
    loadDescription:
      "Something went wrong while loading your billing details. Check your connection and try again.",
  },

  // Stripe connection / setup banners
  connect: {
    notConnectedTitle: "Billing is not connected yet",
    notConnectedBody:
      "Once payments are connected, you can subscribe and add a payment method.",
    setupIssueTitle: "Billing setup issue",
    unavailable: "Payments aren't available right now. Try again in a moment.",
  },

  trialEnded: {
    title: "Your free trial has ended",
    body: "Playback is paused and you can't add devices.",
    ownerCta: "Subscribe below to reactivate your fleet.",
    memberCta: "Ask your workspace owner to subscribe.",
  },

  trialBanner: {
    title: "Free trial · {days} days left",
    body: "No credit card required. Subscribe before {date} to keep your fleet playing.",
  },

  subscription: {
    title: "Subscription",
    licenses: "Licenses",
    licensesInUse: "{used} / {total} in use",
    cancelsOn: "Cancels on",
    renewsOn: "Renews on",
    paymentMethod: "Payment method",
    cardOnFile: "{brand} •••• {last4}",
    cardBrandFallback: "Card",
    noPaymentMethod: "None on file",
    manage: "Manage subscription",
    noSubscriptionYet:
      "No active subscription yet. Choose your device count below to subscribe.",
    ownerOnly:
      "Only the workspace owner can manage billing. Ask your workspace owner to set up payment.",
  },

  badge: {
    trial: "Trial",
    activeCancelling: "Active (cancelling)",
  },

  status: {
    noSubscription: "No subscription",
  },

  configureCard: {
    changePlan: "Change your plan",
    subscribeTitle: "Subscribe · €49.99 / device / month",
    changeBody:
      "Adjust your device count or billing period. Licenses are shared across every workspace you own.",
    subscribeBody:
      "Every feature is included. Pick how many player devices you need, and add or remove licenses anytime.",
    promo:
      "Use code OMWELCOME at checkout for 20% off. The discount applies to every renewal, for as long as you subscribe.",
    updatePlan: "Update plan",
    subscribeCta: "Subscribe & add payment method",
  },

  hardware: {
    title: "Order physical players",
    body: "Buy OmniMedia player hardware to ship to your venues. This is a one-time purchase, entirely separate from your device licenses. Order as many as you need, wherever they're going.",
    quantity: "How many players",
    order: "Order players",
    toast: {
      orderedTitle: "Order placed",
      orderedBody: "We'll ship your players and follow up by email.",
      cancelledTitle: "Order cancelled",
      couldNotOrder: "Could not start the hardware order",
    },
  },

  toast: {
    subscriptionActiveTitle: "Subscription active",
    subscriptionActiveBody:
      "Your payment method is on file and your plan is active.",
    syncPendingTitle: "Payment received, sync pending",
    syncPendingBody: "Refresh this page in a moment.",
    checkoutCancelledTitle: "Checkout cancelled",
    checkoutCancelledBody: "No changes were made to your subscription.",
    planUpdated: "Plan updated",
    couldNotUpdatePlan: "Could not update plan",
    couldNotOpenPortal: "Could not open billing portal",
    billingRefreshed: "Billing refreshed",
    syncFailed: "Sync failed",
    tryAgain: "Please try again.",
  },

  // components/billing/plan-configurator.tsx
  configurator: {
    billing: "Billing",
    monthly: "Monthly",
    yearly: "Yearly",
    yearlyBadge: "Best value",
    loadingPrices: "Loading current pricing…",
    pricesUnavailable:
      "Live pricing is temporarily unavailable. Refresh, or continue to sign-up. You'll always see the exact price before anything is charged.",
    currency: "Currency",
    playerDevices: "Player devices",
    // Unit line under the device stepper. {price} is the catalog unit amount
    // (GET /api/pricing), never a derived figure: CLAIMS F-4 allows no euro
    // amount other than the two list prices, so no per-month equivalent.
    perDevicePerMonth: "{price} per device, per month",
    perDevicePerYear: "{price} per device, per year",
    deviceLine: "{count}× device",
    billedYearly: "(yearly)",
    billedMonthly: "(monthly)",
    // Yearly saving, in whole months rounded from the catalog prices
    // (yearlyMonthsFree). Always "about": 12 × 49.99 − 499.99 = 99.89, which is
    // 0.09 short of two months, so an exact "two months free" is false (F-8).
    // No euro amount for the saving (F-4).
    // {months} is the count spelled as an English word ("two") for 1-12,
    // digits beyond that (monthsFreeLabel in web lib/pricing-copy.ts).
    monthsFree: {
      one: "About {months} month free vs. monthly",
      other: "About {months} months free vs. monthly",
    },
    // Label above the big total (devices × catalog unit price). No tax wording:
    // the pricing surfaces carry none (VAT copy was removed in 7b0d3053 and
    // lint-copy bans "VAT"; CLAIMS C-PRICE-3).
    totalPerYear: "Total per year",
    totalPerMonth: "Total per month",
    decrease: "Decrease {label}",
    increase: "Increase {label}",
  },

  // FALLBACK_CATALOG marketing copy — mirrors the server LICENSE_PRICES and is
  // only rendered when the public pricing API is unreachable.
  fallback: {
    features: {
      allChannels: "Music, ads, announcement & video channels, all included",
      pushToTalk: "Live push-to-talk to any device",
      djMode: "DJ Mode: smooth crossfade on the music channel",
      scheduling: "Multi-location scheduling & playlists",
      kioskSync: "Kiosk lockdown & multi-room synchronized playback",
      userLimit: "Up to 1,000 users per workspace",
      storageLimit: "100 GB media storage per workspace",
    },
  },

  // components/billing/usage-card.tsx
  usage: {
    title: "Included usage",
    devices: "Devices",
    devicesValue: "{active} active / {total} total",
    licensesPurchased: {
      one: "{count} license purchased",
      other: "{count} licenses purchased",
    },
    broadcastsLabel: "Live broadcasts (last {days} days)",
    broadcastsValue: {
      one: "{count} broadcast · {minutes} min",
      other: "{count} broadcasts · {minutes} min",
    },
    deviceMinutes: "{minutes} device-minutes received",
    ttsLabel: "Text-to-speech (this month)",
    ttsValue: "{used} / {cap} chars",
    storageLabel: "Storage (uploads)",
    filesCount: {
      one: "{count} file",
      other: "{count} files",
    },
    purpose: {
      music: "Music",
      ads: "Ads",
      announcements: "Announcements",
      video: "Screen",
    },
    footnote:
      "All of this is included in your flat per-device plan. Nothing here changes your bill.",
  },
} as const;
